import React, { FC, useState } from "react";

import { weatherIcons } from "@utils/mapWeatherIconByName";

import st from "./styles.module.scss";
import cn from "classnames";
import {
    DetalizationWorkState,
    WorkDetalizationData,
} from "../../../../../store/reducers/dashboard/works/detalization";
import { ApiSelect, TextType } from "@baseTypes/baseType";
import { WeatherData } from "../../../../../store/reducers/weather";
import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../../../../../store";
import { identity } from "fp-ts";
import { stickerLoadingWithCrop } from "../../../phases";
import { useTypedSelector } from "@hooks/useTypedSelector";
import Sticker from "../../../../../components/elements/sticker/Sticker";
import FlexLine from "../../../../../components/elements/flexLine/FlexLine";
import ChartHeader from "../../../../../components/elements/chart/ChartHeader";
import Container from "../../../../../components/elements/container/Container";
import { formatNumber2d } from "@utils/format/number";
import { Empty } from "./empty";
import { last } from "lodash";
import { getWorkDetailsRequestParams } from "@pages/dashboard/works/detalization/utils/getWorkDetailsRequestParams";
import { WorkDisplayPeriod, WorkDynamicsVariant } from "@baseTypes/service/dashboard/IWorksDashboardService";
import VariantSwitcher from "@pages/dashboard/works/detalization/dynamics/components/VariantSwitcher/VariantSwitcher";
import DynamicsChart from "@pages/dashboard/works/detalization/dynamics/DynamicsChart/DynamicsChart";

interface IWorkChart {
    listOfWorksForWorksDash: WorkDetalizationData;
    period: WorkDisplayPeriod
}

interface LegendLabel {
    color: string
    size?: 'l' | 'm' | 'xl'
    className?: string
    name: string
    pointerEvents?: boolean
    textClassName?: string
    ellipsis?: boolean
    align?: 'start' | 'end' | 'center'
}

export const CircleLabel: FC<LegendLabel> = (props) => {
    const {
        color,
        name,
        pointerEvents = true,
        size = 'l',
        textClassName,
        ellipsis = false,
        align= 'center'
    } = props;

    const textCls = cn(st['legend_circle__text'], textClassName, ellipsis && st['legend_circle__text--ellipsis'], st[`legend_circle__text--size-${size}`])

    return <div className={cn({ 'as-pointer-none': !pointerEvents }, st.legend_label, st[`legend_label--${align}`], 'as-legend-label', props.className)}>
        <div className={cn(st.legend_circle, 'as-legend-label__circle', st[`legend_circle--size-${size}`])}
             style={{ background: color }}></div>
        <span className={textCls}> {name} </span>
    </div>
}

export const WeatherIcon: FC<{ data?: WeatherData }> = ({ data }) => {
    let icon, temp;

    if(data) {
        temp = data.temp > 0 ? `+${formatNumber2d(data.temp)}` : String(formatNumber2d(data.temp))
        icon = weatherIcons[data.type]
        if (!icon)
            console.warn("Not found icon for type ", data.type)
    }

    return <div className={st.weather_container}>
        <div className={st.weather_icon}><img src={icon || weatherIcons['nodata']} alt="" className={st.weather_icon__image}/></div>
        <div className={st.weather_degree}>{temp || 'Н/Д'}</div>
    </div>
}
export const LineLabel: FC<LegendLabel> = (props) => {
    let { color, name, pointerEvents = true, textClassName } = props
    return <div className={cn({ 'as-pointer-none': !pointerEvents }, st.legend_label)}>
        <div className={st.legend_line} style={{ background: color }}></div>
        <span className={textClassName}> {name} </span>
    </div>
}

const WorkDetailsChart: FC<IWorkChart> = (props) => {
    const { period } = props;

    const {
        data
    } = useTypedSelector<DetalizationWorkState>(state => state.workDetalization)

    const [workDynamicsVariant, setWorkDynamicsVariant] = useState<WorkDynamicsVariant>('cumulative')

    const isFull = period === 'full'
    const workDetalizationPeriod = isFull ? 'detailProgressByDays' : 'detailProgressByHours'

    if (!data[workDetalizationPeriod].cumulative.fact.length && !data[workDetalizationPeriod].cumulative.plan.length) {
        return <Empty name={data[workDetalizationPeriod].workName}/>
    }

    return (
        <Sticker className={st['sticker']}>
            <VariantSwitcher variant={workDynamicsVariant} setVariant={setWorkDynamicsVariant} />
            {data &&
                <>
                    <FlexLine textType={TextType.Header}>
                        <ChartHeader className={st['chart-header']}>
                            <span className={st['chart-header__title']}>Динамика</span>
                            <span className={st['chart-header__total']}>
                                Выполнено за период <strong>{formatNumber2d(last(data[workDetalizationPeriod].cumulative.fact)?.cultivatedArea)} Га</strong>
                            </span>
                        </ChartHeader>
                    </FlexLine>
                    <Container>
                        <DynamicsChart
                            isFull={isFull}
                            isCumulative={workDynamicsVariant === 'cumulative'}
                            weather={data[workDetalizationPeriod].weather}
                            planFactDetalization={data[workDetalizationPeriod][workDynamicsVariant]}
                        />
                    </Container>
                </>}
        </Sticker>
    )
}

const workSelector: ApiSelect<DetalizationWorkState> =
    createSelector((state: RootState) => state.workDetalization, identity.of<DetalizationWorkState>);

export default stickerLoadingWithCrop(
    workSelector,
        actions =>
            (p, params) =>
                actions.fetchWorkDetalization(getWorkDetailsRequestParams(p, params)),
    {cropEnabled: false}
)(WorkDetailsChart);
